import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import { messagingApi } from "messaging/_api/messaging-api";
import ForumQuerySchema, { type ForumQueryJSON } from "messaging/forums/_api/queries/_schemas/forum-query-schema";
import ForumQuery, {
  ForumFilterParameters,
  ForumOrderField,
  ForumOrderParameters,
} from "messaging/forums/_api/queries/forum-query";
import Forum from "messaging/forums/forum";
import { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import Topic from "messaging/forums/topics/topic";
import Session from "users/__session/session";

type ForumStoreState = {
  byId: {
    entries: Record<string, ForumJSON>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byTopic: {
    entries: Record<string, ForumJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byQuery: {
    entries: Record<string, ForumJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  interfaceQueries: {
    entries: Record<string, ForumQueryJSON>;
  };
};

const initialState: ForumStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byTopic: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

export const populateForums = createAsyncThunk<
  ForumJSON[],
  {
    session: Session;
    forumIds: Guid[];
  }
>(
  "forums/getForumsByIds",
  async (
    {
      forumIds,
    }: {
      session: Session;
      forumIds: Guid[];
    },
    thunkAPI
  ) => {
    if (!forumIds.length) return [];

    const store: { forums: ForumStoreState } = thunkAPI.getState() as {
      forums: ForumStoreState;
    };
    const missingForumsIds = _.uniqWith(
      forumIds.filter((forumId) => !store.forums.byId.entries[forumId.value]),
      (a, b) => a.isEqualTo(b)
    );
    if (!missingForumsIds.length) return [];

    const request = thunkAPI.dispatch(messagingApi.endpoints.getForumsByIds.initiate({ ids: missingForumsIds }));
    const onAbort = () => request.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await request.unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
      request.unsubscribe();
    }
  }
);

export const populateForumsByTopic = createAsyncThunk(
  "forums/getForumsByTopic",
  async (
    {
      topic,
    }: {
      session: Session;
      topic: Topic;
    },
    thunkAPI
  ) => {
    try {
      const filterParameters = new ForumFilterParameters({
        context: topic.context,
        entityClass: topic.entityClass,
        entityId: topic.entityId,
      });
      const orderParameters = new ForumOrderParameters({
        orderBy: ForumOrderField.CreatedDate,
        orderDir: OrderDirection.Descending,
      });
      const paginationParameters = PaginationParameters.create({
        pageIndex: 0,
        pageSize: 100,
      });
      const request = new ForumQuery({
        filterParameters,
        orderParameters,
        paginationParameters,
      });
      const apiRequest = thunkAPI.dispatch(
        messagingApi.endpoints.getForums.initiate({
          query: request,
          storeTarget: { kind: "byTopic", topicKey: topic.toString() },
        })
      );
      const onAbort = () => apiRequest.abort();
      thunkAPI.signal.addEventListener("abort", onAbort);
      try {
        return await apiRequest.unwrap();
      } finally {
        thunkAPI.signal.removeEventListener("abort", onAbort);
        apiRequest.unsubscribe();
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const populateForumsByQuery = createAsyncThunk<
  ForumJSON[],
  {
    session: Session;
    query: ForumQuery;
  }
>(
  "forums/getForumsByQuery",
  async (
    {
      query,
    }: {
      session: Session;
      query: ForumQuery;
    },
    thunkAPI
  ) => {
    const request = thunkAPI.dispatch(
      messagingApi.endpoints.getForums.initiate({
        query,
        storeTarget: { kind: "byQuery", queryKey: query.searchParams.toString() },
      })
    );
    const onAbort = () => request.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await request.unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
      request.unsubscribe();
    }
  }
);

const forumsSlice = createSlice({
  name: "forums",
  initialState,
  reducers: {
    addForum: (state, action: PayloadAction<Forum>) => {
      if (action.payload.id) {
        state.byId.entries[action.payload.id.value] = action.payload.toJSON();
      }
      if (action.payload.topic) {
        const topicKey = action.payload.topic.toString();
        if (!state.byTopic.entries[topicKey]) {
          state.byTopic.entries[topicKey] = [];
        }
        state.byTopic.entries[topicKey].push(action.payload.toJSON());
      }
    },
    updateForumInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: ForumQuery;
      }>
    ) => {
      const { interfaceName, query } = action.payload;
      state.interfaceQueries.entries[interfaceName] = ForumQuerySchema.serialize(query);
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(messagingApi.endpoints.getForumsByIds.matchPending, (state, action) => {
      for (const id of action.meta.arg.originalArgs.ids) {
        const forumId = id instanceof Guid ? id.value : id;
        state.byId.loading[forumId] = true;
        state.byId.error[forumId] = null;
      }
    });
    builder.addMatcher(messagingApi.endpoints.getForumsByIds.matchFulfilled, (state, action) => {
      for (const forum of action.payload) {
        if (!forum.id) continue;
        state.byId.entries[forum.id] = forum;
      }
      for (const id of action.meta.arg.originalArgs.ids) {
        const forumId = id instanceof Guid ? id.value : id;
        state.byId.loading[forumId] = false;
        state.byId.error[forumId] = state.byId.entries[forumId] ? null : "Forum not found";
      }
    });
    builder.addMatcher(messagingApi.endpoints.getForumsByIds.matchRejected, (state, action) => {
      for (const id of action.meta.arg.originalArgs.ids) {
        const forumId = id instanceof Guid ? id.value : id;
        state.byId.loading[forumId] = false;
        state.byId.error[forumId] = action.error.message || "Error fetching forum info.";
      }
    });

    builder.addMatcher(messagingApi.endpoints.getForums.matchPending, (state, action) => {
      const storeTarget = action.meta.arg.originalArgs.storeTarget;
      if (storeTarget?.kind === "byTopic") {
        state.byTopic.loading[storeTarget.topicKey] = true;
        state.byTopic.error[storeTarget.topicKey] = null;
      }
      if (storeTarget?.kind === "byQuery") {
        state.byQuery.loading[storeTarget.queryKey] = true;
        state.byQuery.error[storeTarget.queryKey] = null;
      }
    });
    builder.addMatcher(messagingApi.endpoints.getForums.matchFulfilled, (state, action) => {
      const storeTarget = action.meta.arg.originalArgs.storeTarget;
      for (const forum of action.payload) {
        if (!forum.id) continue;
        state.byId.entries[forum.id] = forum;
        state.byId.loading[forum.id] = false;
        state.byId.error[forum.id] = null;
      }
      if (storeTarget?.kind === "byTopic") {
        state.byTopic.entries[storeTarget.topicKey] = action.payload;
        state.byTopic.loading[storeTarget.topicKey] = false;
        state.byTopic.error[storeTarget.topicKey] = null;
      }
      if (storeTarget?.kind === "byQuery") {
        state.byQuery.entries[storeTarget.queryKey] = action.payload;
        state.byQuery.loading[storeTarget.queryKey] = false;
        state.byQuery.error[storeTarget.queryKey] = null;
      }
    });
    builder.addMatcher(messagingApi.endpoints.getForums.matchRejected, (state, action) => {
      const storeTarget = action.meta.arg.originalArgs.storeTarget;
      if (storeTarget?.kind === "byTopic") {
        state.byTopic.loading[storeTarget.topicKey] = false;
        state.byTopic.error[storeTarget.topicKey] = action.error.message || "Failed to load forums for topic";
      }
      if (storeTarget?.kind === "byQuery") {
        state.byQuery.loading[storeTarget.queryKey] = false;
        state.byQuery.error[storeTarget.queryKey] = action.error.message || "Failed to load forums for query";
      }
    });
    builder.addMatcher(messagingApi.endpoints.createForum.matchFulfilled, (state, action) => {
      upsertForumJSON(state, action.payload);
    });
    builder.addMatcher(messagingApi.endpoints.deleteForum.matchFulfilled, (state, action) => {
      const forumId = action.meta.arg.originalArgs.forumId;
      delete state.byId.entries[forumId];
      delete state.byId.loading[forumId];
      delete state.byId.error[forumId];
      removeForumFromLists(state.byTopic.entries, forumId);
      removeForumFromLists(state.byQuery.entries, forumId);
    });
  },
});

function upsertForumJSON(state: ForumStoreState, forum: ForumJSON): void {
  if (!forum.id) return;
  state.byId.entries[forum.id] = forum;
  const forumModel = Forum.fromJSON(forum);
  if (forumModel?.topic) {
    const topicKey = forumModel.topic.toString();
    state.byTopic.entries[topicKey] = [
      ...(state.byTopic.entries[topicKey] ?? []).filter((existingForum) => existingForum.id !== forum.id),
      forum,
    ];
  }
  state.interfaceQueries.entries = {};
  state.byQuery.entries = {};
}

function removeForumFromLists(entries: Record<string, ForumJSON[]>, forumId: string): void {
  for (const key of Object.keys(entries)) {
    entries[key] = entries[key].filter((forum) => forum.id !== forumId);
  }
}

export const { addForum, updateForumInterfaceQuery } = forumsSlice.actions;

const selectForumsByTopic = createSelector(
  [(state: RootState) => state.forums.byTopic.entries, (_: RootState, topic: Topic) => topic.toString()],
  (entries, topicKey) => entries[topicKey]?.map((json) => Forum.fromJSON(json)) ?? undefined
);
export const getForumsByTopic = (topic: Topic) => (state: RootState) => selectForumsByTopic(state, topic);
export const getIsLoadingForumsByTopic = (topic: Topic) => (state: RootState) =>
  state.forums.byTopic.loading[topic.toString()];
export const getErrorLoadingForumsByTopic = (topic: Topic) => (state: RootState) =>
  state.forums.byTopic.error[topic.toString()];

const selectForumsByIds = createSelector(
  [(state: RootState) => state.forums.byId.entries, (_: RootState, ids: Guid[]) => ids],
  (entries, ids) => {
    return ids
      .map((id) => entries[id.value])
      .filter(Boolean)
      .map((json) => Forum.fromJSON(json));
  }
);
export const getForumsByIds = (ids: Guid[]) => (state: RootState) => selectForumsByIds(state, ids);

const selectLoadingForumIds = createSelector([(state: RootState) => state.forums.byId.loading], (loading) => {
  return Object.keys(loading)
    .filter((forumId) => loading[forumId])
    .map((forumId) => Guid.fromJSON(forumId) as Guid);
});
export const getLoadingForumIds = (state: RootState) => selectLoadingForumIds(state);

export const getAreErrorsLoadingForumsByIds = (ids: Guid[]) => (state: RootState) =>
  ids.some((id) => state.forums.byId.error[id.value] !== null);

const selectForumsByQuery = createSelector(
  [(state: RootState) => state.forums.byQuery.entries, (_: RootState, query?: ForumQuery) => query],
  (entries, query) => {
    if (!query) return;
    return entries[query.searchParams.toString()]?.map((json) => Forum.fromJSON(json)) ?? undefined;
  }
);
export const getForumsByQuery = (query?: ForumQuery) => (state: RootState) => selectForumsByQuery(state, query);

const selectForumQueryByInterface = createSelector(
  [(state: RootState) => state.forums.interfaceQueries.entries, (_: RootState, interfaceName: string) => interfaceName],
  (entries, interfaceName) => {
    const queryJson = entries[interfaceName];
    if (!queryJson) return;
    return ForumQuery.fromJSON(queryJson) ?? undefined;
  }
);
export const getForumQueryByInterface = (interfaceName: string) => (state: RootState) =>
  selectForumQueryByInterface(state, interfaceName);

export const getIsLoadingForumsByQuery = (query?: ForumQuery) => (state: RootState) => {
  if (!query) return false;
  return state.forums.byQuery.loading[query.searchParams.toString()] ?? false;
};
export const getErrorLoadingForumsByQuery = (query?: ForumQuery) => (state: RootState) => {
  if (!query) return false;
  return state.forums.byQuery.error[query.searchParams.toString()] ?? false;
};

export type ForumError = {
  id: Guid;
  error: string;
};
const selectAllForumErrors = createSelector([(state: RootState) => state.forums.byId.error], (errors) => {
  return Object.keys(errors)
    .filter((forumId) => forumId && errors[forumId])
    .map((id) => {
      const error = errors[id];
      if (!error) return null;
      return { id: Guid.fromJSON(id) as Guid, error } as ForumError;
    })
    .filter((item): item is ForumError => item !== null);
});
export const getAllForumErrors = (state: RootState) => selectAllForumErrors(state);

export default forumsSlice;
